import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import type {
  SchoolInboxAttachment,
  SchoolInboxChannel,
  SchoolInboxConnection,
  SchoolInboxConnectionInput,
  SchoolInboxConversation,
  SchoolInboxConversationQuery,
  SchoolInboxMessage,
  SchoolInboxMessageInput,
  SchoolInboxNewConversationInput,
  SchoolInboxPage,
  SchoolInboxSignup,
  SchoolInboxTemplate,
} from "~/types/schoolInbox";

const base = "/api/organizations/current/communications/inbox/";

export default class SchoolInboxModule extends HttpFactory {
  constructor(axios: AxiosInstance) {
    super(axios, "school");
  }

  connections() {
    return this.get<SchoolInboxConnection[]>(`${base}connections/`);
  }

  saveConnection(
    channel: SchoolInboxChannel,
    input: SchoolInboxConnectionInput,
  ) {
    return this.patch<SchoolInboxConnection>(
      `${base}connections/${channel}/`,
      input,
    );
  }

  synchronize(channel: SchoolInboxChannel) {
    return this.post<{ queued: boolean }>(
      `${base}connections/${channel}/sync/`,
      {},
    );
  }

  startEmailOAuth(provider: "google" | "microsoft") {
    return this.post<{ authorization_url: string }>(
      `${base}connections/email/oauth/start/`,
      { provider },
    );
  }

  startWhatsApp() {
    return this.post<SchoolInboxSignup>(
      `${base}connections/whatsapp/start/`,
      {},
    );
  }

  completeWhatsApp(input: {
    state: string;
    code: string;
    waba_id: string;
    phone_number_id: string;
  }) {
    return this.post<SchoolInboxConnection>(
      `${base}connections/whatsapp/complete/`,
      input,
    );
  }

  templates() {
    return this.get<SchoolInboxTemplate[]>(
      `${base}connections/whatsapp/templates/`,
    );
  }

  conversations(query: SchoolInboxConversationQuery) {
    return this.get<SchoolInboxPage<SchoolInboxConversation>>(
      `${base}conversations/`,
      { ...query, page_size: query.page_size ?? 25 },
    );
  }

  conversation(id: number) {
    return this.get<SchoolInboxConversation>(`${base}conversations/${id}/`);
  }

  messages(id: number, page = 1) {
    return this.get<SchoolInboxPage<SchoolInboxMessage>>(
      `${base}conversations/${id}/messages/`,
      { page, page_size: 25 },
    );
  }

  createConversation(input: SchoolInboxNewConversationInput) {
    return this.post<{
      conversation: SchoolInboxConversation;
      message: SchoolInboxMessage;
    }>(`${base}conversations/`, input);
  }

  sendMessage(id: number, input: SchoolInboxMessageInput) {
    return this.post<SchoolInboxMessage>(
      `${base}conversations/${id}/messages/`,
      input,
    );
  }

  readConversation(id: number, throughMessageId: number) {
    return this.post<SchoolInboxConversation>(
      `${base}conversations/${id}/read/`,
      { through_message_id: throughMessageId },
    );
  }

  upload(channel: SchoolInboxChannel, file: File) {
    const form = new FormData();
    form.append("channel", channel);
    form.append("file", file);
    return this.post<SchoolInboxAttachment>(`${base}attachments/`, form);
  }

  download(id: number) {
    return this.get<Blob>(
      `${base}attachments/${id}/download/`,
      {},
      { responseType: "blob" },
    );
  }
}
